import React, { useEffect, useState } from "react";
import { ExternalLink } from "lucide-react";
import { api } from "@/api/client";
import { agencyApi } from "./agencyApi";
import { StatusPill, groupDocs } from "./AgencyBits";

// A document's href is EITHER an external review link (Frame.io/Vimeo/YouTube — always fine
// to open directly) OR our own proxy stream route (`file_url`, cookie-authed, same-origin) —
// NEVER a raw Drive URL/file id. See modules/build.py Document Room (Wave 3, 2026-09).
const docHref = (d) => d.external_url || (d.file_url ? `${api.defaults.baseURL}/v1/build${d.file_url}` : "#");

export default function AgencyDocuments() {
  const [groups, setGroups] = useState(null);
  const [sharedDocs, setSharedDocs] = useState(null);

  useEffect(() => {
    agencyApi.sharedDocuments().then((r) => setSharedDocs(r.data.documents || [])).catch(() => setSharedDocs([]));
    // Perf fix (2026-10, Cursor audit item 7 / group 0B): these per-project document fetches
    // are fully independent of each other — was a serial for/await loop (one project waits on
    // the previous one's round trip); now fired in parallel. Same `agencyApi.documents(p.id)`
    // call, same per-project try/catch-and-skip behaviour, just not one-at-a-time.
    agencyApi.myProjects().then(async (r) => {
      const projects = r.data.projects || [];
      const results = await Promise.all(projects.map((p) =>
        agencyApi.documents(p.id).then((d) => ({ project: p, docs: d.data.documents || [] })).catch(() => null)
      ));
      setGroups(results.filter((g) => g && g.docs.length));
    }).catch(() => setGroups([]));
  }, []);

  if (!groups) return <div className="ag-small">Loading…</div>;

  return (
    <div data-testid="agency-documents">
      <h1 className="ag-h1 ag-entrance" style={{ fontSize: 36, marginBottom: 8 }}>Documents</h1>
      <p className="ag-body ag-entrance" style={{ color: "var(--muted)", marginBottom: 30 }}>Statements of work, invoices and shared files across your productions.</p>
      {sharedDocs && sharedDocs.length > 0 && (
        <section className="ag-entrance-2" style={{ marginBottom: 34 }} data-testid="agency-documents-shared-section">
          <h2 className="ag-eyebrow" style={{ marginBottom: 14 }}>Shared with your agency</h2>
          <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
            {groupDocs(sharedDocs).map(({ bucket, docs: bdocs }) => (
              <div key={bucket} data-testid={`agency-documents-shared-bucket-${bucket.replace(/[\s&]+/g, "-").toLowerCase()}`}>
                <div className="ag-label" style={{ marginBottom: 10 }}>{bucket}</div>
                <div className="ag-stack">
                  {bdocs.map((d) => (
                    <a key={d.id} className="ag-card ag-card-hover" href={docHref(d)} target="_blank" rel="noopener noreferrer"
                      style={{ display: "flex", justifyContent: "space-between", alignItems: "center", textDecoration: "none", color: "inherit" }} data-testid="agency-documents-shared-row">
                      <div className="ag-body" style={{ fontWeight: 600 }}>{d.name} <span className="ag-small">· {d.doc_type} · v{d.version}</span></div>
                      <div style={{ display: "flex", alignItems: "center", gap: 14 }}><StatusPill status={d.status} /><ExternalLink size={16} color="#8A8A8A" /></div>
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      {groups.length === 0 && (!sharedDocs || sharedDocs.length === 0) ? (
        <div className="ag-empty">No documents have been shared with you yet.</div>
      ) : (
        <div className="ag-entrance-2" style={{ display: "flex", flexDirection: "column", gap: 34 }}>
          {groups.map(({ project, docs }) => (
            <section key={project.id}>
              <h2 className="ag-eyebrow" style={{ marginBottom: 14 }}>{project.name}</h2>
              <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
                {groupDocs(docs).map(({ bucket, docs: bdocs }) => (
                  <div key={bucket} data-testid={`agency-documents-bucket-${bucket.replace(/[\s&]+/g, "-").toLowerCase()}`}>
                    <div className="ag-label" style={{ marginBottom: 10 }}>{bucket}</div>
                    <div className="ag-stack">
                      {bdocs.map((d) => (
                        <a key={d.id} className="ag-card ag-card-hover" href={docHref(d)} target="_blank" rel="noopener noreferrer"
                          style={{ display: "flex", justifyContent: "space-between", alignItems: "center", textDecoration: "none", color: "inherit" }} data-testid="agency-documents-row">
                          <div className="ag-body" style={{ fontWeight: 600 }}>{d.name} <span className="ag-small">· {d.doc_type} · v{d.version}</span></div>
                          <div style={{ display: "flex", alignItems: "center", gap: 14 }}><StatusPill status={d.status} /><ExternalLink size={16} color="#8A8A8A" /></div>
                        </a>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
